import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Package, Upload, ArrowRightLeft, Wrench,
  Trash2, Tag, Printer, ClipboardCheck, BarChart2, Settings,
  Users, MapPin, FolderOpen, Building2, Truck, UserCheck,
  ChevronRight, LogOut, Bell, User, Search, Calculator,
  ShieldCheck, Shield, FileText, RefreshCw, TrendingUp, Radio, MessageSquare, KeyRound, Eye, EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../services/api';
import { Modal, Field, Spinner } from '../common';

// Change Password for the signed-in user (POST /auth/change-password): current
// password, then the new one twice. Same strength rule as the server:
// 8+ characters with upper and lower case, a number and a special character.
const PWD_RULE = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[@$!%*#?&^_-]).{8,}$/;
export const ChangePasswordModal = ({ open, onClose, forced }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', new_password_confirmation: '' });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ current_password: '', new_password: '', new_password_confirmation: '' }); }, [open]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const weak = form.new_password && !PWD_RULE.test(form.new_password);
  const mismatch = form.new_password_confirmation && form.new_password !== form.new_password_confirmation;
  const same = form.new_password && form.new_password === form.current_password;
  const ready = form.current_password && form.new_password && !weak && !mismatch && !same && form.new_password_confirmation;
  const save = async () => {
    if (!ready) return;
    setSaving(true);
    try {
      const r = await authApi.changePassword(form);
      // The server signs out every session and hands this one a fresh pair, so it stays signed in.
      try {
        if (r.data?.accessToken)  localStorage.setItem('ams_token', r.data.accessToken);
        if (r.data?.refreshToken) localStorage.setItem('ams_refresh', r.data.refreshToken);
      } catch {}
      toast.success(r.message || 'Password changed');
      onClose(true);
    } catch {
      // The server's message (e.g. "Current password is incorrect") is shown by the API error handler.
    } finally { setSaving(false); }
  };
  const type = show ? 'text' : 'password';
  return (
    <Modal open={open} onClose={() => onClose(false)} title="Change Password"
      footer={<>
        <button className="btn btn-secondary" onClick={() => onClose(false)}>{forced ? 'Later' : 'Cancel'}</button>
        <button className="btn btn-primary" onClick={save} disabled={!ready || saving}>{saving ? <Spinner size="sm" /> : 'Change Password'}</button>
      </>}>
      {forced && <p style={{ fontSize:'var(--fs-sm)', color:'var(--text-secondary)', marginTop:0 }}>Your account was set up with a temporary password. Please choose your own.</p>}
      <Field label="Current Password" required>
        <input className="form-control" type={type} autoComplete="current-password" value={form.current_password} onChange={set('current_password')} />
      </Field>
      <Field label="New Password" required error={weak ? 'At least 8 characters, with upper and lower case, a number and a special character (@$!%*#?&^_-)' : same ? 'Must be different from the current password' : null}
        hint={!weak && !same ? '8+ characters: upper and lower case, a number and a special character' : null}>
        <input className="form-control" type={type} autoComplete="new-password" value={form.new_password} onChange={set('new_password')} />
      </Field>
      <Field label="Confirm New Password" required error={mismatch ? 'Passwords don\'t match' : null}>
        <input className="form-control" type={type} autoComplete="new-password" value={form.new_password_confirmation} onChange={set('new_password_confirmation')}
          onKeyDown={e => { if (e.key === 'Enter') save(); }} />
      </Field>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShow(v => !v)}>
        {show ? <EyeOff size={13} /> : <Eye size={13} />} {show ? 'Hide' : 'Show'} passwords
      </button>
    </Modal>
  );
};

// Each leaf carries the :view permission that decides whether it shows in
// the sidebar at all. A group with no visible children is hidden entirely.
// Dashboard has no perm — every logged-in user sees it regardless of role.
const NAV = [
  { section: 'Overview' },
  { label: 'Dashboard',    icon: LayoutDashboard, to: '/' },
  { section: 'Operations' },
  {
    label: 'Assets', icon: Package,
    children: [
      { label: 'Asset List',   icon: Package,    to: '/assets',      perm: 'assets:assets:view' },
      { label: 'FAR Upload',   icon: Upload,     to: '/far-upload',  perm: 'assets:far_upload:view' },
      { label: 'RFID Upload',  icon: Radio,      to: '/rfid-upload', perm: 'assets:far_upload:view' },
      { label: 'Tag Mapping',  icon: Tag,        to: '/tag-mapping', perm: 'assets:tags:view' },
      { label: 'Tag Printing', icon: Printer,    to: '/tag-print',   perm: 'assets:tags:view' },
    ]
  },
  {
    label: 'Transactions', icon: ArrowRightLeft,
    children: [
      { label: 'Transfers',    icon: ArrowRightLeft, to: '/transfers',  perm: 'transactions:transfers:view' },
      { label: 'Scrap/Disposal', icon: Trash2,      to: '/disposals',   perm: 'transactions:disposals:view' },
      { label: 'Maintenance',  icon: Wrench,         to: '/maintenance', perm: 'transactions:maintenance:view' },
      { label: 'Check-In/Out', icon: RefreshCw,      to: '/checkouts',   perm: 'transactions:checkouts:view' },
      { label: 'Insurance',   icon: Shield,         to: '/insurance',   perm: 'transactions:insurance:view' },
    ]
  },
  { label: 'Audit',         icon: ClipboardCheck, to: '/audit',        perm: 'audit:sessions:view' },
  { section: 'Finance & Reports' },
  { label: 'CAPEX',         icon: TrendingUp,     to: '/capex',        perm: 'capex:view' },
  { label: 'Depreciation',  icon: Calculator,     to: '/depreciation', perm: 'depreciation:view' },
  {
    label: 'Reports', icon: BarChart2,
    children: [
      { label: 'Asset Register',    icon: FileText,   to: '/reports/asset-register', perm: 'reports:asset_register:view' },
      { label: 'Reconciliation',    icon: BarChart2,  to: '/reports/reconciliation', perm: 'reports:operational:view' },
      { label: 'Depreciation Rpt',  icon: Calculator, to: '/reports/depreciation',   perm: 'reports:financial:view' },
      { label: 'Transfer History',  icon: ArrowRightLeft, to: '/reports/transfers',  perm: 'reports:operational:view' },
    ]
  },
  { section: 'Administration' },
  {
    label: 'Masters', icon: Settings,
    children: [
      { label: 'Users',           icon: Users,     to: '/masters/users',     perm: 'users:users:view' },
      { label: 'Roles',           icon: ShieldCheck, to: '/masters/roles',   perm: 'users:roles:view' },
      { label: 'Company Hier.',   icon: Building2, to: '/masters/company',   perm: 'masters:company_masters:view' },
      { label: 'Locations',       icon: MapPin,    to: '/masters/locations', perm: 'masters:locations:view' },
      { label: 'Categories',      icon: FolderOpen, to: '/masters/categories', perm: 'masters:categories:view' },
      { label: 'Vendors',         icon: Truck,     to: '/masters/vendors',   perm: 'masters:vendors:view' },
      { label: 'Custodians',      icon: UserCheck, to: '/masters/custodians', perm: 'masters:custodians:view' },
    ]
  },
  // Admin-gated by role code, not permission — GET /reports itself 403s for
  // anyone who isn't an Admin (IssueReportController::assertAdmin), so a
  // permission-based entry would need a matching catalog entry; the role
  // check here mirrors that backend gate directly instead.
  { label: 'Support', icon: MessageSquare, to: '/support', adminOnly: true },
];

// A group (Assets, Transactions, …) is an accordion section: only one is
// open at a time, the one holding the current page opens by itself, and its
// header stays highlighted while you're on one of its pages — open or not.
const NavGroup = ({ item, children, open, onToggle }) => {
  const location = useLocation();
  const active   = children.some(c => location.pathname.startsWith(c.to));
  const Icon = item.icon;
  return (
    <div className={`nav-group${open ? ' open' : ''}${active ? ' has-active' : ''}`}>
      <button className="nav-group-toggle" onClick={onToggle} aria-expanded={open} title={item.label}>
        <span className="nav-icon"><Icon size={16} /></span>
        <span className="nav-label">{item.label}</span>
        <ChevronRight size={14} className="nav-chevron" />
      </button>
      <div className="nav-submenu-wrap">
        <div className="nav-submenu">
          {children.map(c => {
            const CI = c.icon;
            return (
              <NavLink key={c.to} to={c.to} title={c.label}
                className={({ isActive }) => `nav-subitem${isActive?' active':''}`}>
                <CI size={14} /><span>{c.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const Sidebar = () => {
  const { user, logout, can } = useAuth();
  // Opened from "Change Password" below; also once on sign-in for an account
  // still on its temporary password (must_change_pwd).
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForced, setPwdForced] = useState(false);
  useEffect(() => {
    if (user?.must_change_pwd && !sessionStorage.getItem('ams_pwd_prompted')) {
      try { sessionStorage.setItem('ams_pwd_prompted', '1'); } catch {}
      setPwdForced(true); setPwdOpen(true);
    }
  }, [user?.must_change_pwd]);
  const isAdmin = user?.roles?.some(r => r.code === 'ADMIN');
  const location = useLocation();

  // What this user can see: groups keep only their permitted children (and
  // vanish with none), and a section label shows only above visible entries.
  const visible = [];
  NAV.forEach(item => {
    if (item.section) { visible.push(item); return; }
    if (item.children) {
      const kids = item.children.filter(c => can(c.perm));
      if (kids.length) visible.push({ ...item, kids });
      return;
    }
    if (item.perm && !can(item.perm)) return;
    if (item.adminOnly && !isAdmin) return;
    visible.push(item);
  });
  const entries = visible.filter((item, i) => !item.section || (visible[i + 1] && !visible[i + 1].section));

  // The group holding the current page opens on navigation; clicking another
  // header switches to it, clicking the open one closes it.
  const groupOf = path => visible.find(it => it.kids?.some(c => path.startsWith(c.to)))?.label || null;
  const [openGroup, setOpenGroup] = useState(() => groupOf(location.pathname));
  useEffect(() => { const g = groupOf(location.pathname); if (g) setOpenGroup(g); }, [location.pathname]);
  const initials = (user?.full_name || '?').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <nav className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-mark"><Tag size={17} color="#fff" /></div>
        <div>
          <div className="sidebar-logo-text">AssetTrack</div>
          <div className="sidebar-logo-sub">{user?.company_name || 'Pro'}</div>
        </div>
      </div>

      <div className="sidebar-nav">
        {entries.map((item, i) => {
          if (item.section) return <div key={i} className="nav-section-label">{item.section}</div>;
          if (item.kids) return <NavGroup key={i} item={item} children={item.kids} open={openGroup === item.label}
            onToggle={() => setOpenGroup(g => (g === item.label ? null : item.label))} />;
          const Icon = item.icon;
          return (
            <NavLink key={i} to={item.to} end={item.to === '/'} title={item.label}
              className={({ isActive }) => `nav-item${isActive?' active':''}`}>
              <span className="nav-icon"><Icon size={16} /></span><span className="nav-label">{item.label}</span>
            </NavLink>
          );
        })}
      </div>

      <div className="sidebar-user">
        <div className="sidebar-user-card">
          <div className="sidebar-avatar">{initials}</div>
          <div className="sidebar-user-text">
            <div className="sidebar-user-name">{user?.full_name}</div>
            <div className="sidebar-user-mail">{user?.email}</div>
          </div>
        </div>
        <div className="sidebar-user-actions">
          <button className="sidebar-action" title="Change Password" onClick={() => { setPwdForced(false); setPwdOpen(true); }}>
            <KeyRound size={14} /><span>Password</span>
          </button>
          <button className="sidebar-action danger" title="Sign Out" onClick={logout}>
            <LogOut size={14} /><span>Sign Out</span>
          </button>
        </div>
      </div>
      <ChangePasswordModal open={pwdOpen} forced={pwdForced} onClose={() => setPwdOpen(false)} />
    </nav>
  );
};

export const Topbar = ({ title }) => {
  const { user } = useAuth();
  return (
    <div className="topbar">
      <div style={{ flex:1 }}>
        <span style={{ fontSize:'var(--fs-md)', fontWeight:600, color:'var(--text-primary)' }}>{title}</span>
      </div>
      <button className="btn btn-ghost btn-icon" style={{ position:'relative' }}>
        <Bell size={17} />
        <span style={{ position:'absolute', top:4, right:4, width:7, height:7, background:'var(--danger)', borderRadius:'50%', border:'1.5px solid var(--white)' }} />
      </button>
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 10px', background:'var(--bg)', borderRadius:'var(--r-md)', fontSize:'var(--fs-sm)' }}>
        <div style={{ width:26, height:26, background:'var(--primary-light)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <User size={13} color="var(--primary)" />
        </div>
        <span style={{ fontWeight:500, color:'var(--text-primary)' }}>{user?.full_name?.split(' ')[0]}</span>
      </div>
    </div>
  );
};

export const Layout = ({ children, title }) => (
  <div className="app-shell">
    <Sidebar />
    <div className="main-wrap">
      <Topbar title={title} />
      <main className="page">{children}</main>
    </div>
  </div>
);
